import { useEffect, useState } from "react";
import {
  PencilIcon,
  XIcon,
  PlusIcon,
  TrashIcon,
  MailIcon,
} from "@heroicons/react/outline";
import EditMainInfoModal from "./EditMainInfoModal";
import "react-quill-new/dist/quill.snow.css";
import ReactQuill from "react-quill-new";
import {
  getEventOrganizers,
  addEventOrganizer,
  removeEventOrganizer,
} from "../../../api/eventsApi";
import ToastContainer from "../../../components/toastContainer";
import Toast from "../../../components/toast";

const InfoRow = ({ label, value, children }) => (
  <div className="mb-2">
    <p className="text-slate-400">{label}</p>
    {children ? children : <p className="text-white">{value || "-"}</p>}
  </div>
);

const InfoLinkRow = ({ label, value }) => (
  <div className="mb-2">
    <p className="text-slate-400">{label}</p>
    {value ? (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-pink-500 hover:underline break-all"
      >
        {value}
      </a>
    ) : (
      <p className="text-white">-</p>
    )}
  </div>
);

// ── Invite Organiser Modal ──
const InviteOrganiserModal = ({ onClose, onSubmit, saving }) => {
  const [form, setForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
  });
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (!form.email.trim()) {
      setError("Email is required.");
      return;
    }
    onSubmit(form);
  };

  const inputCls =
    "w-full bg-[#0d1424] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-pink-500/40 transition";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <h2 className="text-base font-semibold text-white">
              Invite Organiser
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              New users will receive a set-password email. Existing users will
              be attached silently.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-white transition p-1 rounded-lg hover:bg-slate-800"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5 font-medium">
              Email <span className="text-pink-500">*</span>
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => {
                setForm((p) => ({ ...p, email: e.target.value }));
                setError("");
              }}
              placeholder="organiser@example.com"
              className={inputCls}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5 font-medium">
                First Name{" "}
                <span className="text-slate-600 normal-case tracking-normal font-normal">
                  (optional)
                </span>
              </label>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, first_name: e.target.value }))
                }
                placeholder="Jane"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5 font-medium">
                Last Name{" "}
                <span className="text-slate-600 normal-case tracking-normal font-normal">
                  (optional)
                </span>
              </label>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, last_name: e.target.value }))
                }
                placeholder="Doe"
                className={inputCls}
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              <svg
                className="w-3.5 h-3.5 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className={`px-5 py-2 text-sm rounded-lg text-white font-medium transition ${saving ? "bg-pink-400 cursor-not-allowed" : "bg-pink-600 hover:bg-pink-700"}`}
          >
            {saving ? "Sending…" : "Send Invite"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Tab ──
const MainInfoTab = ({
  data,
  onUpdateMain,
  onUpdateDescription,
  onUpdateOrganiser,
  loading,
  isViewOnly,
}) => {
  const [showMainModal, setShowMainModal] = useState(false);

  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [descValue, setDescValue] = useState("");

  const [isEditingOrganiser, setIsEditingOrganiser] = useState(false);
  const [organiserForm, setOrganiserForm] = useState({
    show_organiser_details: false,
    organiser_name: "",
    organiser_company_name: "",
    organiser_company_reg_no: "",
    organiser_vat_no: "",
    organiser_logo: "",
  });
  const [logoPreview, setLogoPreview] = useState("");

  // ── Event organiser invite state ──
  const [organisers, setOrganisers] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [inviteToast, setInviteToast] = useState(null);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const formatArray = (arr) => (arr?.length ? arr.join(", ") : "-");

  useEffect(() => {
    setDescValue(data?.description || "");
    setOrganiserForm({
      show_organiser_details: data?.show_organiser_details || false,
      organiser_name: data?.organiser_name || "",
      organiser_company_name: data?.organiser_company_name || "",
      organiser_company_reg_no: data?.organiser_company_reg_no || "",
      organiser_vat_no: data?.organiser_vat_no || "",
      organiser_logo: data?.organiser_logo || "",
    });
    setLogoPreview(data?.organiser_logo || "");
    if (data?.id) fetchOrganisers();
  }, [data]);

  const fetchOrganisers = async () => {
    try {
      const result = await getEventOrganizers(data.id);
      setOrganisers(Array.isArray(result) ? result : result.results || []);
    } catch (err) {
      // silent — not critical
    }
  };

  const handleInviteSubmit = async (form) => {
    try {
      setInviteSaving(true);
      const payload = { email: form.email };
      if (form.first_name) payload.first_name = form.first_name;
      if (form.last_name) payload.last_name = form.last_name;
      await addEventOrganizer(data.id, payload);
      setShowInviteModal(false);
      setInviteToast({
        type: "success",
        message: "Organiser added successfully.",
      });
      fetchOrganisers();
    } catch (err) {
      setInviteToast({
        type: "error",
        message:
          err?.response?.data?.detail ||
          err?.detail ||
          "Failed to add organiser.",
      });
    } finally {
      setInviteSaving(false);
    }
  };

  const handleRemoveOrganiser = async (userId) => {
    try {
      setRemovingId(userId);
      await removeEventOrganizer(data.id, userId);
      setOrganisers((prev) => prev.filter((o) => o.id !== userId));
    } catch (err) {
      setInviteToast({
        type: "error",
        message: err?.response?.data?.detail || "Failed to remove organiser.",
      });
    } finally {
      setRemovingId(null);
    }
  };

  const handleOrganiserChange = (key, value) => {
    setOrganiserForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLogoPreview(ev.target.result);
      setOrganiserForm((prev) => ({ ...prev, organiser_logo: file }));
    };
    reader.readAsDataURL(file);
  };

  const handleOrganiserCancel = () => {
    setIsEditingOrganiser(false);
    setOrganiserForm({
      show_organiser_details: data?.show_organiser_details || false,
      organiser_name: data?.organiser_name || "",
      organiser_company_name: data?.organiser_company_name || "",
      organiser_company_reg_no: data?.organiser_company_reg_no || "",
      organiser_vat_no: data?.organiser_vat_no || "",
      organiser_logo: data?.organiser_logo || "",
    });
    setLogoPreview(data?.organiser_logo || "");
  };

  const handleOrganiserSave = () => {
    onUpdateOrganiser(organiserForm);
    setIsEditingOrganiser(false);
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* LEFT — Main Info */}
      <div className="bg-[#131C31] border border-white/10 rounded-lg p-4 relative">
        {!isViewOnly && (
          <button
            onClick={() => setShowMainModal(true)}
            className="absolute top-3 right-3 text-slate-400 hover:text-white"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
        )}

        <InfoRow label="Event Name" value={data?.name} />
        <InfoLinkRow label="Link" value={data?.public_page_url} />
        <InfoRow
          label="Channels"
          value={formatArray(data?.distribution_channels)}
        />
        <InfoRow label="Categories" value={data?.category} />
        <InfoRow label="Venue" value={data?.map_address} />
        <InfoRow label="Start" value={formatDate(data?.date_start)} />
        <InfoRow label="End" value={formatDate(data?.date_end)} />
        <InfoRow
          label="Event Specific Type"
          value={data?.event_specific_type}
        />
        <InfoRow label="Dress Code" value={data?.dress_code} />

        <div className="mt-2 mb-2">
          <p className="text-slate-400 mb-1">Artist Lineup</p>
          {data?.event_artists?.length > 0 ? (
            <div className="flex flex-wrap gap-3">
              {data.event_artists.map((artist) => (
                <div
                  key={artist.id}
                  className="flex items-center gap-2 bg-slate-800 border border-white/10 px-3 py-2 rounded-lg"
                >
                  <img
                    src={artist.profile_image}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <span className="text-sm text-white">{artist.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No Artists</p>
          )}
        </div>

        {data?.client_commission_percentage !== 0 && (
          <InfoRow
            label="Platform Commission"
            value={`${data?.client_commission_percentage}%`}
          />
        )}
        <InfoRow
          label="VAT included in commission"
          value={data?.commission_vat_included ? "YES" : "NO"}
        />
        <InfoRow label="VAT" value={`${data?.commission_vat_percentage}%`} />

        <InfoRow label="Currency" value={`${data?.currency}`} />

        <InfoRow
          label="Can buy without login"
          value={data?.can_buy_without_login ? "YES" : "NO"}
        />
        
        <InfoRow label="Meta Tags" value={formatArray(data?.meta_tags)} />

        <div className="mt-4">
          <p className="text-slate-400 mb-1">Flyers</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-400 mb-1">Horizontal</p>
              {data?.flyer_horizontal ? (
                <a
                  href={data.flyer_horizontal}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={data.flyer_horizontal}
                    alt="Horizontal Flyer"
                    className="w-96 h-32 object-contain rounded-lg border border-white/10 hover:opacity-80 transition"
                  />
                </a>
              ) : (
                <div className="w-57 h-32 flex items-center justify-center bg-slate-800 rounded-lg text-slate-500 text-xs">
                  No Image
                </div>
              )}
            </div>

            <div>
              <p className="text-xs text-slate-400 mb-1">Portrait</p>
              {data?.flyer_portrait ? (
                <a
                  href={data.flyer_portrait}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={data.flyer_portrait}
                    alt="Portrait Flyer"
                    className="w-20 h-32 object-contain rounded-lg border border-white/10 hover:opacity-80 transition"
                  />
                </a>
              ) : (
                <div className="w-20 h-32 flex items-center justify-center bg-slate-800 rounded-lg text-slate-500 text-xs">
                  No Image
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT — Description + Organiser Details + Invite Organisers */}
      <div className="flex flex-col gap-4">
        {/* Description */}
        <div className="bg-[#131C31] border border-white/10 rounded-lg p-4 relative">
          {!isViewOnly && (
            <button
              onClick={() => setIsEditingDesc((prev) => !prev)}
              className="absolute top-3 right-3 text-slate-400 hover:text-white"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          )}
          <p className="text-slate-400 mb-2">Description</p>
          {!isViewOnly && isEditingDesc ? (
            <>
              <div className="bg-[#131C31] rounded-lg overflow-hidden">
                <ReactQuill
                  theme="snow"
                  value={descValue}
                  onChange={setDescValue}
                  modules={{
                    toolbar: [
                      [{ header: [2, 3, false] }],
                      ["bold", "italic", "underline", "strike"],
                      [{ align: [] }],
                      ["link"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      ["clean"],
                    ],
                  }}
                />
              </div>
              <div className="flex justify-end gap-3 mt-3">
                <button
                  onClick={() => {
                    setIsEditingDesc(false);
                    setDescValue(data?.description || "");
                  }}
                  className="px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onUpdateDescription(descValue);
                    setIsEditingDesc(false);
                  }}
                  className="px-3 py-1.5 text-sm text-white bg-pink-600 hover:bg-pink-700 rounded"
                >
                  Save
                </button>
              </div>
            </>
          ) : (
            <div className="bg-slate-800 rounded-lg p-3">
              <div
                className="ql-editor text-white"
                dangerouslySetInnerHTML={{
                  __html: data?.description || "<p>No description</p>",
                }}
              />
            </div>
          )}
        </div>

        {/* Organiser Details */}
        <div className="bg-[#131C31] border border-white/10 rounded-lg p-4 relative">
          {!isViewOnly && (
            <button
              onClick={() =>
                isEditingOrganiser
                  ? handleOrganiserCancel()
                  : setIsEditingOrganiser(true)
              }
              className="absolute top-3 right-3 text-slate-400 hover:text-white"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          )}
          <p className="text-slate-400 mb-3">Organiser Details</p>

          {!isViewOnly && isEditingOrganiser ? (
            <>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-400 mb-2 block">
                    Show organiser details on event page
                  </label>
                  <div className="flex items-center gap-6">
                    {[true, false].map((val) => (
                      <label
                        key={String(val)}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="show_organiser_details"
                          checked={organiserForm.show_organiser_details === val}
                          onChange={() =>
                            handleOrganiserChange("show_organiser_details", val)
                          }
                          className="accent-pink-500"
                        />
                        <span className="text-sm text-white">
                          {val ? "Yes" : "No"}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1.5">
                    Company Logo (500 x 500 Recommended)
                  </p>
                  <div className="flex items-center gap-4">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Company logo"
                        className="w-14 h-14 rounded-lg object-contain border border-white/10 bg-slate-800"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-slate-800 border border-white/10 flex items-center justify-center text-slate-500 text-xs">
                        No logo
                      </div>
                    )}
                    <label className="cursor-pointer px-3 py-1.5 text-sm text-slate-300 border border-white/10 rounded-lg hover:bg-slate-800 transition">
                      Upload
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleLogoChange}
                      />
                    </label>
                  </div>
                </div>
                {[
                  {
                    key: "organiser_name",
                    label: "Organiser Name",
                    placeholder: "Organiser name",
                  },
                  {
                    key: "organiser_company_name",
                    label: "Company Name",
                    placeholder: "Company name",
                  },
                  {
                    key: "organiser_company_reg_no",
                    label: "Company Reg No",
                    placeholder: "e.g. 12345678",
                  },
                  {
                    key: "organiser_vat_no",
                    label: "Company VAT No",
                    placeholder: "e.g. 12345678",
                  },
                ].map(({ key, label, placeholder }) => (
                  <div key={key}>
                    <label className="text-xs text-slate-400 mb-1 block">
                      {label}
                    </label>
                    <input
                      type="text"
                      value={organiserForm[key]}
                      onChange={(e) =>
                        handleOrganiserChange(key, e.target.value)
                      }
                      className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                      placeholder={placeholder}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={handleOrganiserCancel}
                  className="px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleOrganiserSave}
                  className="px-3 py-1.5 text-sm text-white bg-pink-600 hover:bg-pink-700 rounded"
                >
                  Save
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <InfoRow
                label="Show on event page"
                value={data?.show_organiser_details ? "Yes" : "No"}
              />
              <div className="flex items-center gap-3">
                {data?.organiser_logo ? (
                  <img
                    src={data.organiser_logo}
                    alt="Company logo"
                    className="w-14 h-14 rounded-lg object-contain border border-white/10 bg-slate-800"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-slate-800 border border-white/10 flex items-center justify-center text-slate-500 text-xs">
                    No logo
                  </div>
                )}
              </div>
              <div className="border-t border-white/5 pt-3 space-y-2">
                <InfoRow label="Organizer Name" value={data?.organiser_name} />
                <InfoRow
                  label="Company Name"
                  value={data?.organiser_company_name}
                />
                <InfoRow
                  label="Company Reg No"
                  value={data?.organiser_company_reg_no}
                />
                <InfoRow
                  label="Company VAT No"
                  value={data?.organiser_vat_no}
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Invite Organisers (Dashboard View Only Access) ── */}
        {!isViewOnly && (
          <div className="bg-[#131C31] border border-white/10 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-slate-300 text-sm font-medium">
                  Event Organizers
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Users with view-only access to this event's dashboard
                </p>
              </div>
              <button
                onClick={() => setShowInviteModal(true)}
                className="flex items-center gap-1.5 text-xs font-medium text-pink-400 hover:text-pink-300 bg-pink-500/10 hover:bg-pink-500/15 border border-pink-500/20 px-3 py-1.5 rounded-lg transition"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                Invite
              </button>
            </div>

            {organisers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <MailIcon className="w-6 h-6 text-slate-600 mb-2" />
                <p className="text-xs text-slate-500">
                  No organizers invited yet
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {organisers.map((org) => (
                  <div
                    key={org.id}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 bg-slate-800/50 rounded-lg border border-white/5"
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">
                        {org.first_name || org.last_name
                          ? `${org.first_name || ""} ${org.last_name || ""}`.trim()
                          : org.email}
                      </p>
                      {(org.first_name || org.last_name) && (
                        <p className="text-xs text-slate-500 truncate">
                          {org.email}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveOrganiser(org.id)}
                      disabled={removingId === org.id}
                      className="shrink-0 p-1.5 rounded-md text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition disabled:opacity-40"
                      title="Remove organiser"
                    >
                      {removingId === org.id ? (
                        <svg
                          className="w-3.5 h-3.5 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8z"
                          />
                        </svg>
                      ) : (
                        <TrashIcon className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {!isViewOnly && (
        <EditMainInfoModal
          isOpen={showMainModal}
          onClose={() => setShowMainModal(false)}
          data={data}
          onSubmit={onUpdateMain}
        />
      )}

      {!isViewOnly && showInviteModal && (
        <InviteOrganiserModal
          onClose={() => setShowInviteModal(false)}
          onSubmit={handleInviteSubmit}
          saving={inviteSaving}
        />
      )}

      <ToastContainer>
        {inviteToast && (
          <div className="toast-enter">
            <Toast
              type={inviteToast.type}
              message={inviteToast.message}
              onClose={() => setInviteToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default MainInfoTab;
